import React, { useState } from "react";
import { Divider, Flex, Text } from "@chakra-ui/react";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import PathPickMembershipHotDealLimitInfoModal from "@/features/pathpick/features/membership/components/plan-compare-section/PathPickMembershipHotDealLimitInfoModal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  FIRST_MONTH_DISCOUNT_RATE,
  MEMBERSHIP_BLUE_SHADOW,
  MEMBERSHIP_CARD_SHADOW,
  MEMBERSHIP_COLORS,
  MEMBERSHIP_FEATURE_ROWS,
  selectMembershipPlanPriceView,
} from "@/features/pathpick/features/membership/constants";
import {
  MembershipBillingCycle,
  MembershipPlan,
  MembershipPlanRelation,
} from "@/features/pathpick/features/membership/types";

interface Props {
  plan: MembershipPlan;
  billingCycle: MembershipBillingCycle;
  relation: MembershipPlanRelation;
  // 신규 가입이면 첫 달 50% 할인가와 정가 취소선을 함께 보여준다.
  isFirstSubscription: boolean;
  // 플랜 시작/업다운그레이드 클릭 → 플랜 변경 모달 오픈 (enterprise는 상담하기만 노출)
  onChangeRequest?: (plan: MembershipPlan) => void;
  // 하단 CTA 노출 여부 — 끄면 한도/가격만 읽는 카드가 된다(시작 가이드).
  hasCta?: boolean;
  // 무료체험 가능 바이어에게 신규(none) CTA를 [무료체험|플랜 시작하기] 두 버튼으로 가른다(시안).
  onStartTrial?: () => void;
  // 상담하기(enterprise) 동작 override — 주지 않으면 기본대로 채널톡을 연다.
  // 결제를 접수하지 않는 화면(패스픽 랜딩)에서 멤버십 페이지로 넘기는 용도다.
  onConsult?: (plan: MembershipPlan) => void;
  // 이 플랜으로 접수한 신청이 입금 대기 중 — CTA가 "신청 중"이 된다.
  isApplicationPending?: boolean;
}

interface ChipProps {
  label: string;
}

const Chip = ({ label }: ChipProps) => {
  return (
    <Flex
      padding={"3px 7px"}
      alignItems={"center"}
      borderRadius={"full"}
      background={MEMBERSHIP_COLORS.blueBg}
      flexShrink={0}
    >
      <Text textStyle={"body-sm-bold"} color={MEMBERSHIP_COLORS.blueDeep}>
        {label}
      </Text>
    </Flex>
  );
};

interface MembershipPlanCardCtaProps {
  plan: MembershipPlan;
  relation: MembershipPlanRelation;
  onChangeRequest?: (plan: MembershipPlan) => void;
  // 무료체험을 아직 쓸 수 있는 바이어에게는 신규(none) CTA가
  // [무료체험(보조) | 플랜 시작하기(주)] 두 버튼으로 갈라진다(시안).
  onStartTrial?: () => void;
  onConsult?: (plan: MembershipPlan) => void;
  isApplicationPending?: boolean;
}

// relation → CTA 라벨. "none"은 현재 플랜이 없는 신규 가입이라 업/다운그레이드가 아니라 "시작하기"다.
const CTA_LABEL_KEYS: Record<MembershipPlanRelation, string> = {
  current: "membership:plan_card.cta_current",
  upgrade: "membership:plan_card.cta_upgrade",
  downgrade: "membership:plan_card.cta_downgrade",
  none: "membership:plan_card.cta_start",
};

// relation과 무관하게 "접수 후 입금 대기" 상태에서 쓰는 라벨이라 위 Record에 넣지 않는다.
const CTA_LABEL_KEY_PENDING = "membership:plan_card.cta_pending";

function PathPickMembershipPlanCardCta({
  plan,
  relation,
  onChangeRequest,
  onStartTrial,
  onConsult,
  isApplicationPending = false,
}: MembershipPlanCardCtaProps) {
  const { t } = useI18nHelper();

  // 시안 버튼 스타일 — 보조(현재/상담)는 카드색 배경+파란 보더, 주(신청류)는 파란 배경.
  const secondaryProps = {
    marginTop: "auto",
    height: "40px",
    borderRadius: "10px",
    border: `1px solid ${MEMBERSHIP_COLORS.blueBorder}`,
    background: MEMBERSHIP_COLORS.cardBg,
  };
  const primaryProps = {
    marginTop: "auto",
    height: "40px",
    borderRadius: "10px",
    background: MEMBERSHIP_COLORS.blue,
  };

  // 현재 플랜이 enterprise인 경우도 있으므로 current 분기를 먼저 둔다
  if (relation === "current") {
    return (
      <AppButton
        variant={"white"}
        width={"100%"}
        isDisabled
        chakraProps={secondaryProps}
      >
        <Text
          textStyle={"body-sm-bold"}
          fontWeight={800}
          color={MEMBERSHIP_COLORS.blueDeep}
        >
          {t(CTA_LABEL_KEYS.current)}
        </Text>
      </AppButton>
    );
  }

  // 접수돼 입금 대기 중인 플랜 — 다시 신청받지 않고 접수 내용을 보여준다.
  // current와 달리 비활성화하지 않는다. 눌러서 요약 모달을 열어야 한다.
  if (isApplicationPending) {
    return (
      <AppButton
        variant={"white"}
        width={"100%"}
        onClick={() => onChangeRequest?.(plan)}
        chakraProps={secondaryProps}
      >
        <Text
          textStyle={"body-sm-bold"}
          fontWeight={800}
          color={MEMBERSHIP_COLORS.blueDeep}
        >
          {t(CTA_LABEL_KEY_PENDING)}
        </Text>
      </AppButton>
    );
  }

  if (plan.id === "enterprise") {
    return (
      <AppButton
        variant={"white"}
        width={"100%"}
        onClick={() =>
          onConsult ? onConsult(plan) : ChannelService.showMessenger()
        }
        chakraProps={secondaryProps}
      >
        <Text
          textStyle={"body-sm-bold"}
          fontWeight={800}
          color={MEMBERSHIP_COLORS.blueDeep}
        >
          {t("membership:plan_card.cta_consult")}
        </Text>
      </AppButton>
    );
  }

  // 무료체험 가능 바이어의 신규 CTA — [무료체험(보조) | 플랜 시작하기(주)] 두 버튼(시안).
  // 체험 없이 바로 신청하는 경로를 항상 열어둔다.
  if (relation === "none" && onStartTrial) {
    return (
      <Flex gap={"8px"} marginTop={"auto"}>
        <AppButton
          variant={"white"}
          width={"100%"}
          onClick={onStartTrial}
          chakraProps={{ ...secondaryProps, marginTop: undefined, flex: 1 }}
        >
          <Text
            textStyle={"body-sm-bold"}
            fontWeight={800}
            color={MEMBERSHIP_COLORS.blueDeep}
          >
            {t("membership:plan_card.cta_trial")}
          </Text>
        </AppButton>
        <AppButton
          variant={"primary"}
          width={"100%"}
          onClick={() => onChangeRequest?.(plan)}
          chakraProps={{ ...primaryProps, marginTop: undefined, flex: 1 }}
        >
          <Text textStyle={"body-sm-bold"} fontWeight={800} color={"#FFFFFF"}>
            {t(CTA_LABEL_KEYS.none)}
          </Text>
        </AppButton>
      </Flex>
    );
  }

  // 업/다운그레이드는 신청 접수가 아니라 상담(채널톡)으로 안내한다 —
  // 결제가 수동(입금)이라 변경은 상담을 거쳐 처리한다(현재 멤버십 카드의 안내 문구와 동일 정책).
  if (relation === "upgrade" || relation === "downgrade") {
    return (
      <AppButton
        variant={"primary"}
        width={"100%"}
        onClick={() => ChannelService.showMessenger()}
        chakraProps={primaryProps}
      >
        <Text textStyle={"body-sm-bold"} fontWeight={800} color={"#FFFFFF"}>
          {t(CTA_LABEL_KEYS[relation])}
        </Text>
      </AppButton>
    );
  }

  return (
    <AppButton
      variant={"primary"}
      width={"100%"}
      onClick={() => onChangeRequest?.(plan)}
      chakraProps={primaryProps}
    >
      <Text textStyle={"body-sm-bold"} fontWeight={800} color={"#FFFFFF"}>
        {t(CTA_LABEL_KEYS[relation])}
      </Text>
    </AppButton>
  );
}

function PathPickMembershipPlanCard({
  plan,
  billingCycle,
  relation,
  isFirstSubscription,
  onChangeRequest,
  hasCta = true,
  onStartTrial,
  onConsult,
  isApplicationPending,
}: Props) {
  const { t, tNumberString } = useI18nHelper();
  const [isHotDealLimitInfoOpen, setIsHotDealLimitInfoOpen] =
    useState<boolean>(false);

  const isYearly = billingCycle === "yearly";
  const isCurrent = relation === "current";
  const formatWon = (value: number) =>
    t("membership:price.won", { amount: tNumberString(value) });

  // enterprise처럼 가격이 없는 플랜은 null → 상담 안내 박스로 대체한다.
  const price = selectMembershipPlanPriceView(
    plan,
    billingCycle,
    isFirstSubscription
  );

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      padding={"16px"}
      border={
        isCurrent
          ? `1px solid ${MEMBERSHIP_COLORS.blue}`
          : `1px solid ${MEMBERSHIP_COLORS.cardBorder}`
      }
      borderRadius={"14px"}
      background={MEMBERSHIP_COLORS.cardBg}
      boxShadow={isCurrent ? MEMBERSHIP_BLUE_SHADOW : MEMBERSHIP_CARD_SHADOW}
    >
      <Flex flexDirection={"column"} gap={"4px"}>
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"8px"}
        >
          <Text
            textStyle={"body-lg-bold"}
            fontSize={"18px"}
            color={MEMBERSHIP_COLORS.textStrong}
            minWidth={0}
          >
            {plan.englishName}
          </Text>
          {isCurrent && (
            <Chip label={t("membership:plan_card.current_badge")} />
          )}
        </Flex>
        <Text textStyle={"body-sm-regular"} color={MEMBERSHIP_COLORS.textMuted}>
          {t(`membership:plan_card.${plan.id}.tagline`)}
        </Text>
      </Flex>

      <Divider borderColor={"#EFEFED"} />

      {/* 가격 블록 — 가격이 없는 플랜(enterprise)은 상담 안내 박스 */}
      {price === null ? (
        <Flex
          flexDirection={"column"}
          gap={"4px"}
          padding={"12px"}
          borderRadius={"8px"}
          background={"#F7F9FA"}
        >
          <Text
            textStyle={"body-sm-bold"}
            color={MEMBERSHIP_COLORS.textFaint}
          >
            {t("membership:plan_card.price.enterprise_label")}
          </Text>
          <Text
            textStyle={"body-lg-bold"}
            fontSize={"20px"}
            color={MEMBERSHIP_COLORS.blueDeep}
          >
            {t("membership:plan_card.price.enterprise_value")}
          </Text>
          <Text textStyle={"body-sm-regular"} color={MEMBERSHIP_COLORS.textMuted}>
            {t("membership:plan_card.price.enterprise_note")}
          </Text>
        </Flex>
      ) : (
        <Flex flexDirection={"column"} gap={"4px"}>
          <Text textStyle={"body-sm-bold"} color={MEMBERSHIP_COLORS.textFaint}>
            {t(
              isYearly
                ? "membership:plan_card.price.yearly_label"
                : "membership:plan_card.price.monthly_label"
            )}
          </Text>
          {/* 프로모션일 때만 정가를 취소선으로 함께 보여준다 */}
          {price.listPrice !== null && (
            <Text
              textStyle={"body-sm-regular"}
              color={MEMBERSHIP_COLORS.textFaint}
              textDecoration={"line-through"}
            >
              {formatWon(price.listPrice)}
            </Text>
          )}
          <Flex gap={"4px"} alignItems={"baseline"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={{ base: "20px", md: "23px" }}
              color={MEMBERSHIP_COLORS.blueDeep}
            >
              {formatWon(price.chargedPrice)}
            </Text>
            <Text textStyle={"body-sm-bold"} color={MEMBERSHIP_COLORS.amber}>
              {/* 월 결제 프로모션은 "/ 월" 대신 첫 달 할인율을 붙인다 */}
              {isYearly
                ? t("membership:plan_card.price.per_year")
                : isFirstSubscription
                ? t("membership:plan_card.price.first_month_badge", {
                    rate: FIRST_MONTH_DISCOUNT_RATE,
                  })
                : t("membership:plan_card.price.per_month")}
            </Text>
          </Flex>
          {isYearly ? (
            <Flex
              justifyContent={"space-between"}
              alignItems={"flex-start"}
              gap={"8px"}
              flexWrap={"wrap"}
              rowGap={"4px"}
            >
              {price.monthlyEquivalent !== null && (
                <Text
                  textStyle={"body-sm-regular"}
                  color={MEMBERSHIP_COLORS.textMuted}
                >
                  {t("membership:plan_card.price.monthly_equivalent", {
                    amount: tNumberString(price.monthlyEquivalent),
                  })}
                </Text>
              )}
              <Flex
                flexDirection={"column"}
                gap={"2px"}
                alignItems={"flex-end"}
                textAlign={"right"}
              >
                <Text
                  textStyle={"body-sm-bold"}
                  color={MEMBERSHIP_COLORS.amber}
                >
                  {t("membership:plan_card.price.discount_badge", {
                    rate: price.yearlyDiscountRate,
                  })}
                </Text>
                {isFirstSubscription && (
                  <Text
                    textStyle={"caption-medium"}
                    color={MEMBERSHIP_COLORS.blueDeep}
                  >
                    {t("membership:plan_card.price.promo_discount_note", {
                      rate: FIRST_MONTH_DISCOUNT_RATE,
                    })}
                  </Text>
                )}
              </Flex>
            </Flex>
          ) : (
            <Text
              textStyle={"body-sm-regular"}
              color={MEMBERSHIP_COLORS.textMuted}
            >
              {/* 첫 달 할인가 뒤에는 둘째 달부터의 정가를 반드시 함께 안내한다 */}
              {isFirstSubscription && plan.monthlyPrice !== null
                ? t("membership:plan_card.price.next_month_note", {
                    amount: tNumberString(plan.monthlyPrice),
                  })
                : t("membership:plan_card.price.monthly_note")}
            </Text>
          )}
        </Flex>
      )}

      {/* 제공 항목 행 — 값이 없는 행은 건너뛴다 (오더액 기준 수수료는 enterprise 전용) */}
      {MEMBERSHIP_FEATURE_ROWS.map((row) => {
        const valueKey = plan.featureValueKeys[row.id];
        if (!valueKey) return null;

        return (
          <React.Fragment key={row.id}>
            <Divider borderColor={"#EFEFED"} />
            {row.id === "hot_deal" ? (
              // 시안: 하이라이트 박스가 좌우로 -7px 튀어나가 안의 텍스트는
              // 다른 행들과 같은 선에 정렬된다(박스 패딩 7px과 상쇄).
              <Flex
                flexDirection={"column"}
                gap={"4px"}
                padding={"9px 7px"}
                margin={"0 -7px"}
                borderRadius={"8px"}
                background={MEMBERSHIP_COLORS.blueBg}
              >
                <Text
                  textStyle={"body-sm-medium"}
                  fontWeight={600}
                  color={MEMBERSHIP_COLORS.textFaint}
                >
                  {t(`membership:${row.labelKey}`)}
                </Text>
                <Text
                  textStyle={"body-sm-bold"}
                  color={MEMBERSHIP_COLORS.textStrong}
                >
                  {t(`membership:${valueKey}`)}
                </Text>
              </Flex>
            ) : (
              <Flex flexDirection={"column"} gap={"2px"}>
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text
                    textStyle={"body-sm-medium"}
                    fontWeight={600}
                    color={MEMBERSHIP_COLORS.textFaint}
                  >
                    {t(`membership:${row.labelKey}`)}
                  </Text>
                  {row.hasInfoModal && (
                    <Flex
                      as={"button"}
                      alignItems={"center"}
                      cursor={"pointer"}
                      onClick={() => setIsHotDealLimitInfoOpen(true)}
                    >
                      <CustomIcon
                        src={"info-circle"}
                        width={14}
                        height={14}
                        color={MEMBERSHIP_COLORS.textFaint}
                      />
                    </Flex>
                  )}
                </Flex>
                <Text
                  textStyle={"body-sm-bold"}
                  color={MEMBERSHIP_COLORS.textStrong}
                >
                  {t(`membership:${valueKey}`)}
                </Text>
              </Flex>
            )}
          </React.Fragment>
        );
      })}

      {hasCta && (
        <PathPickMembershipPlanCardCta
          plan={plan}
          relation={relation}
          onChangeRequest={onChangeRequest}
          onStartTrial={onStartTrial}
          onConsult={onConsult}
          isApplicationPending={isApplicationPending}
        />
      )}

      {isHotDealLimitInfoOpen && (
        <PathPickMembershipHotDealLimitInfoModal
          isOpen
          onClose={() => setIsHotDealLimitInfoOpen(false)}
        />
      )}
    </Flex>
  );
}

export default PathPickMembershipPlanCard;
